/**
 * Standardized Admin Page Layout Component
 * Based on the warranty page pattern
 * 
 * Provides consistent:
 * - Container sizing (max-w-7xl mx-auto)
 * - Padding (p-6)
 * - Spacing (space-y-6)
 * - Header with title, subtitle, and actions
 */

import React, { ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface AdminPageLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
}

export function AdminPageLayout({ title, subtitle, children, actions }: AdminPageLayoutProps) {
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{title}</h1>
          {subtitle && (
            <p className="text-gray-600 mt-2">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2">
            {actions}
          </div>
        )}
      </div>

      {/* Page Content */}
      {children}
    </div>
  );
}

interface StatsGridProps {
  children: ReactNode;
  cols?: 2 | 3 | 4 | 5 | 6;
}

export function StatsGrid({ children, cols = 4 }: StatsGridProps) {
  const colsClass = {
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5',
    6: 'lg:grid-cols-6'
  }[cols];

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 ${colsClass} gap-4`}>
      {children}
    </div>
  );
}
